import React, { useContext } from "react"
import { View } from "react-native"
import { Checkbox } from "react-native-paper"
import moment from "moment"
import { validation } from "@data/utility/validation"
import { formatHoursPerWeek } from "@common/utils/creditCalculation"
import { partialMonthHours } from "@common/utils/partialMonthHours"
import IconButtonM from "@common/components/button/IconButtonM"
import { HolidayCourseSelector } from "@common/components/subscription/HolidayCourseSelector"
import SelectBox from "@common/components/select/selectSingle-Multiple"
import { theme } from "../../theme/theme"
import { DimensionsContext } from "../../utils/mobileUtils"
import TextButtonM from "../../components/button/TextButtonM"
import TextM from "../../components/text/TextM"
import { objectivesTab } from "../../../student/account/onboarding/RegisterTrialSessionScreen"
import HourAllocationStep from "../../student-diagnostic-stepper/HourAllocationStep"
import { TabSelect } from "../../components/select/TabSelect"
import ConstraintsGridStep from "../../student-diagnostic-stepper/ConstraintsGridStep"
import { DiscountCard } from "../../../new-subscription/components/DiscountCard"
import { getOfferTypeOptions } from "@data/constants/offersConstants"

/*
 * @Description
 * use this hook each time you need some steps from StudentTutoringRequestUpdate or NewSubscriptionScreen
 * Feel free to add new steps if needed (just keep StudentTutoringRequestUpdate unchanged as a reference)
 */
export const useNewSubscriptionStepper = () => {
    const dimensions = useContext(DimensionsContext)

    // ------------------------------------------------------------
    // Offer type step
    // ------------------------------------------------------------
    const offerTypeStep = ({ withCommitment, setWithCommitment }) => {
        return {
            label: "Format",
            name: "offer_type",
            type: "select.single",
            options: getOfferTypeOptions(),
            defaultValue: null,
            validationRules: { ...validation.required() },
            topComponent: (
                <Checkbox.Item
                    label="Avec engagement"
                    position={"leading"}
                    onPress={() => setWithCommitment(!withCommitment)}
                    color={theme.colors.primary}
                    labelStyle={{ fontSize: 14, textAlign: "left" }}
                    style={{ marginHorizontal: 5, marginTop: 5 }}
                    status={withCommitment ? "checked" : "indeterminate"}
                />
            ),
        }
    }

    // ------------------------------------------------------------
    // Offer plan step
    // ------------------------------------------------------------
    const calculateOffersOptions = ({
        offerType,
        pricingTable,
        sessionsPerWeek,
        withCommitment,
    }) => {
        if (!offerType) {
            return []
        }
        const allAmounts = pricingTable?.[offerType]
        if (!allAmounts) return []
        const offersKeys = Object.keys(allAmounts)

        const specificOffersOptions = (commitmentKey) =>
            offersKeys?.map((hoursPerMonth) => {
                const hoursPerWeek = sessionsPerWeek?.[hoursPerMonth]
                const hourlyPrice = allAmounts[hoursPerMonth]?.[commitmentKey]
                const hourlyPriceDisplay =
                    Math.round(
                        allAmounts[hoursPerMonth]?.[commitmentKey] * 10
                    ) / 10
                const monthlyPrice =
                    Math.round(hourlyPrice * hoursPerMonth * 10) / 10

                const prefix =
                    offerType === "integral" && withCommitment
                        ? "à partir de "
                        : ""

                return {
                    label: `${formatHoursPerWeek(hoursPerWeek)} /semaine - ${prefix}${hourlyPriceDisplay}€ /h`,
                    description: `${hoursPerMonth}h /mois - ${prefix}${monthlyPrice}€ /mois`,
                    value: parseInt(hoursPerMonth),
                    hourlyPrice,
                }
            })

        // return a sectioned array, of offers, first section with commitment, second without
        // start with commitment
        return withCommitment
            ? specificOffersOptions("commitment")
            : specificOffersOptions("base")
    }
    const offerPlanStep = ({
        offerType,
        pricingTable,
        sessionsPerWeek,
        withCommitment,
        getHourlyPrice,
        defaultValue,
    }) => {
        const options = calculateOffersOptions({
            offerType,
            pricingTable,
            sessionsPerWeek,
            withCommitment,
        })

        const monthlyPriceMapping = options.reduce((acc, option) => {
            acc[option.value] = option.hourlyPrice
            return acc
        }, {})
        const step = {
            label: "Offre",
            name: "hours_per_month",
            type: "select.single",
            options,
            defaultValue: defaultValue ?? null,
        }

        if (!getHourlyPrice) {
            return step
        }

        return {
            // monthlyPriceMapping is an object that maps the hours per month to the hourly price
            // { [hoursPerMonth]: hourlyPrice, [hoursPerMonth]: hourlyPrice, ... }
            metadata: { monthlyPriceMapping },
            step,
        }
    }

    // ------------------------------------------------------------
    // Subscription start date step
    // ------------------------------------------------------------

    const calculateTutorialTime = (sessionTime) => {
        if (!sessionTime) return null

        const [hours, minutes] = sessionTime.split(":").map(Number)
        let tutorialMinutes = minutes - 15
        let tutorialHours = hours

        if (tutorialMinutes < 0) {
            tutorialHours -= 1
            tutorialMinutes = 60 + tutorialMinutes
        }

        return `${tutorialHours.toString().padStart(2, "0")}:${tutorialMinutes
            .toString()
            .padStart(2, "0")}`
    }
    const subscriptionStartDateStep = ({
        holiday: {
            holidayCourses,
            setHolidayCourses,
            holidayCourseOptions,
            schoolSubjects,
        } = {
            holidayCourseOptions: [],
            holidayCourses: [],
            setHolidayCourses: () => {},
            schoolSubjects: [],
        },
        tutorial: { needsTutorial, setNeedsTutorial } = {
            needsTutorial: undefined,
            setNeedsTutorial: () => {},
        },
        subscriptionStartDate,
    }) => {
        const withHoliday = holidayCourseOptions?.length > 0
        const addStageComponent = withHoliday ? (
            <HolidayCourseSelector
                holidayCourses={holidayCourses}
                setHolidayCourses={setHolidayCourses}
                holidayCourseOptions={holidayCourseOptions}
                schoolSubjects={schoolSubjects}
            />
        ) : null

        const withTutorial = needsTutorial !== undefined

        return {
            label: "Début de l'accompagnement",
            type: "date-time",
            name: "starts_at",
            defaultValue: moment()
                .add(1, "day")
                .set({ hour: 18, minute: 0 })
                .format("YYYY-MM-DD HH:mm"),
            topComponent: (
                <>
                    {addStageComponent}
                    <TextM
                        color={theme.colors.primary}
                        wrap
                        style={{
                            marginTop: 25,
                            marginBottom: -6,
                            alignSelf: "center",
                        }}
                        fontSize={14}
                    >
                        Première séance régulière
                    </TextM>
                </>
            ),
            bottomComponent: withTutorial && (
                <>
                    <Checkbox.Item
                        label="Besoin d'un tutoriel avant la première séance"
                        position={"leading"}
                        value={needsTutorial}
                        onPress={() => setNeedsTutorial(!needsTutorial)}
                        color={theme.colors.primary}
                        labelStyle={{
                            fontSize: 14,
                            textAlign: "left",
                        }}
                        style={{
                            marginHorizontal: 5,
                            marginTop: 5,
                        }}
                        status={needsTutorial ? "checked" : "unchecked"}
                    />
                    {needsTutorial && (
                        <TextM
                            wrap
                            color={theme.colors.primary}
                            style={{ marginLeft: 15, marginTop: 5 }}
                            fontSize={12}
                        >
                            Le tutoriel aura lieu à{" "}
                            {calculateTutorialTime(
                                subscriptionStartDate?.split(" ")?.[1]
                            )}
                        </TextM>
                    )}
                </>
            ),
            validationRules: {
                ...validation.required(),
            },
        }
    }

    // ------------------------------------------------------------
    // TTC price options
    // ------------------------------------------------------------
    /**
     * Returns the list of selectable hourly-price options for the tarif step.
     *
     * @param offerType       - "initial" | "essential" | "integral"
     * @param hoursPerMonth   - selected volume (e.g. 9)
     * @param pricingTableV2  - full pricing table from Redux
     * @param withCommitment  - boolean
     */
    const calculateTtcOptions = ({
        offerType,
        hoursPerMonth,
        pricingTableV2,
        withCommitment,
    }) => {
        const baseHourlyPrice =
            pricingTableV2?.[offerType]?.[hoursPerMonth]?.base
        const minHourlyPrice =
            pricingTableV2?.[offerType]?.[hoursPerMonth]?.commitment
        const baseMonthlyPrice = baseHourlyPrice * hoursPerMonth
        const minMonthlyPrice = Math.round(minHourlyPrice * hoursPerMonth)
        const overallMinHourlyPrice =
            pricingTableV2?.[offerType]?.[22]?.commitment
        const overallMinMonthlyPrice = Math.round(
            overallMinHourlyPrice * hoursPerMonth
        )

        if (offerType !== "integral" && withCommitment) {
            const options = [
                {
                    label: `${minMonthlyPrice}€ /mois - ${minHourlyPrice?.toFixed(1)}€ /h`,
                    value: minHourlyPrice,
                },
            ]

            if (minHourlyPrice !== overallMinHourlyPrice) {
                options.push({
                    label: `${overallMinMonthlyPrice}€ /mois - ${
                        Math.round(overallMinHourlyPrice * 100) / 100
                    }€ /h (Tarif famille)`,
                    value: overallMinHourlyPrice,
                })
            }

            // Tarif social — cours partagés uniquement, validation manager.
            // Les paliers éligibles sont portés par la grille backend : `social`
            // n'y existe qu'aux paliers 1h et 2h. On teste donc la PRÉSENCE du
            // tarif, sans re-coder de plafond d'heures ici — un plafond côté
            // client divergeait dès que la grille bougeait.
            const socialHourlyPrice =
                pricingTableV2?.[offerType]?.[hoursPerMonth]?.social
            if (socialHourlyPrice) {
                const socialMonthlyPrice = Math.round(
                    socialHourlyPrice * hoursPerMonth
                )
                options.push({
                    label: `${socialMonthlyPrice}€ /mois - ${socialHourlyPrice?.toFixed(1)}€ /h (Tarif social)`,
                    value: socialHourlyPrice,
                })
            }

            return options
        } else if (baseHourlyPrice && minHourlyPrice) {
            const options = []
            for (
                let price = minHourlyPrice;
                price <= baseHourlyPrice;
                price += 0.5
            ) {
                const discount = Math.round(
                    ((baseHourlyPrice - price) / baseHourlyPrice) * 100
                )
                const monthlyPrice = Math.round(price * hoursPerMonth)
                options.push({
                    label: `${monthlyPrice}€ /mois - ${price?.toFixed(1)}€ /h`,
                    value: price,
                    description: `-${discount}%`,
                })
            }
            if (!options.find((option) => option.value === baseHourlyPrice)) {
                options.push({
                    label: `${baseMonthlyPrice}€ /mois - ${baseHourlyPrice?.toFixed(1)}€ /h`,
                    value: baseHourlyPrice,
                    description: "-0%",
                })
            }
            options.sort((a, b) => a.value - b.value)
            return options.reverse()
        }
        return []
    }

    // ------------------------------------------------------------
    // Tarif step
    // ------------------------------------------------------------
    /**
     * @param offerType       - "initial" | "essential" | "integral"
     * @param hoursPerMonth   - selected volume (e.g. 9)
     * @param pricingTableV2  - full pricing table from Redux
     * @param withCommitment  - boolean
     * @param defaultValue    - pre-selected value (falls back to first option)
     * @param discount        - optional object to enable discount fields:
     *   { showDiscountFields, setShowDiscountFields,
     *     discountValueOptions, discountDurationOptions,
     *     formValues, setFormValues }
     */
    const tarifStep = ({
        offerType,
        hoursPerMonth,
        pricingTableV2,
        withCommitment,
        defaultValue,
        discount = null,
    }) => {
        const ttcOptions = calculateTtcOptions({
            offerType,
            hoursPerMonth,
            pricingTableV2,
            withCommitment,
        })
        const discountInputs = discount
            ? [
                  {
                      name: "discount_type",
                      label: "Type de remise",
                      sliderLabel: "Type de remise",
                      options: [
                          { label: "Pourcentage (%)", value: "percentage" },
                          { label: "Montant (€)", value: "flat" },
                      ],
                      hidden: !discount.showDiscountFields,
                      onChange: (val) => {
                          discount.setFormValues({
                              ...discount.formValues,
                              discount_type: val,
                              discount_value: null,
                          })
                      },
                  },
                  {
                      name: "discount_value",
                      label: "Montant de la remise",
                      sliderLabel: "Montant de la remise",
                      options: discount.discountValueOptions,
                      hidden: !discount.showDiscountFields,
                      defaultValue: 10,
                  },
                  {
                      name: "discount_duration",
                      label: "Valable",
                      sliderLabel: "Valable",
                      options: discount.discountDurationOptions,
                      hidden: !discount.showDiscountFields,
                      defaultValue: 1,
                  },
              ]
            : []

        return {
            label: "Tarif",
            type: "multi.select.single",
            inputs: [
                {
                    name: "ttc_price",
                    label: "Tarif TTC",
                    sliderLabel: "Tarif TTC",
                    options: ttcOptions,
                    defaultValue: defaultValue ?? ttcOptions?.[0]?.value,
                },
                ...discountInputs,
            ],
            ...(discount && {
                bottomComponent: !discount.showDiscountFields ? (
                    <TextButtonM
                        label={"+ Ajouter une remise éphémère"}
                        onPress={() => discount.setShowDiscountFields(true)}
                        fontWeight="Bold"
                        color={theme.colors.primary}
                        style={{ alignSelf: "center", marginTop: 10 }}
                    />
                ) : null,
            }),
            showNextButton: true,
        }
    }

    // ------------------------------------------------------------
    // School subjects step
    // ------------------------------------------------------------
    const schoolSubjectsStep = ({
        desiredSupport,
        setDesiredSupport,
        schoolSubjects,
        defaultPrioritizedSchoolSubjects,
        prioritizedSchoolSubjects,
        hoursPerWeek,
        label,
        showLimitReachedText = false,
        discountDeadline,
        ...props
    }) => {
        const isHomeworkSupport = desiredSupport === "Aide aux devoirs"
        const selectedSchoolSubjects = prioritizedSchoolSubjects
            ?.map((x) => schoolSubjects?.find((y) => y?.id === x)?.name)
            .join(", ")
        const displayedSchoolSubjects =
            selectedSchoolSubjects !== ""
                ? selectedSchoolSubjects
                : isHomeworkSupport
                ? "Toutes les matières"
                : "-"

        const defaultValue =
            defaultPrioritizedSchoolSubjects?.map((x) => x?.id) ?? []

        const maxSelection = isHomeworkSupport ? hoursPerWeek * 2 : hoursPerWeek
        const isLimitReached = prioritizedSchoolSubjects?.length >= maxSelection
        const limitText = isHomeworkSupport
            ? isLimitReached
                ? `✓ Limite atteinte (${prioritizedSchoolSubjects?.length}/${maxSelection} matières) • Le tuteur pourra aider sur d'autres matières selon les devoirs du jour`
                : `${prioritizedSchoolSubjects?.length} matière${
                      prioritizedSchoolSubjects?.length > 1 ? "s" : ""
                  } prioritaires choisie${
                      prioritizedSchoolSubjects?.length > 1 ? "s" : ""
                  } • Le tuteur pourra aider sur d'autres matières selon les devoirs du jour`
            : isLimitReached
            ? `✓ Limite atteinte (${prioritizedSchoolSubjects?.length}/${maxSelection} matières) • Il faut au minimum 1h/semaine par matière`
            : `${prioritizedSchoolSubjects?.length} matière${
                  prioritizedSchoolSubjects?.length > 1 ? "s" : ""
              } choisie${prioritizedSchoolSubjects?.length > 1 ? "s" : ""}`
        const captionText = showLimitReachedText
            ? limitText
            : "Matières sélectionnées : " + displayedSchoolSubjects

        const dimensions = useContext(DimensionsContext)

        return {
            label: label ?? "Accompagnement souhaité",
            topComponent: (
                <View style={{ alignSelf: "center", width: "100%", flex: 1 }}>
                    <DiscountCard discountDeadline={discountDeadline} />
                    <TabSelect
                        options={objectivesTab}
                        selected={desiredSupport}
                        onSelect={setDesiredSupport}
                        cardStyle={{
                            flex: 1,
                            minWidth:
                                dimensions.width > 800
                                    ? 400
                                    : dimensions.width - 80,
                        }}
                    />
                </View>
            ),
            bottomComponent: (
                <View
                    style={{
                        alignSelf: "center",
                        width: "100%",
                        flex: 1,
                        marginBottom: 10,
                    }}
                >
                    <TextM
                        wrap
                        color={
                            showLimitReachedText && isLimitReached
                                ? theme.colors.success
                                : theme.colors.grey
                        }
                    >
                        {captionText}
                    </TextM>
                </View>
            ),
            ordered: isHomeworkSupport,
            type: "select.multiple",
            name: "prioritized_school_subjects",
            options: schoolSubjects?.map((x) => ({
                label: x?.name,
                value: x?.id,
                icon: x?.icon,
            })),
            defaultValue,
            validationRules: { ...validation.required() },
            maxSelection,
            showNextButton: prioritizedSchoolSubjects?.length > 0,
            reRenderDeps: [desiredSupport],
            ...props,
        }
    }

    // ------------------------------------------------------------
    //  Subjects hours allocation step
    // ------------------------------------------------------------
    const generateDefaultSubjectHoursAllocation = (
        selectedSubjects,
        hoursPerWeek
    ) => {
        const baseHours = Math.floor(hoursPerWeek / selectedSubjects.length)
        const extraHours = hoursPerWeek % selectedSubjects.length

        const allocation = {}
        selectedSubjects.forEach((subjectId, index) => {
            allocation[subjectId] = baseHours + (index < extraHours ? 1 : 0)
        })
        return allocation
    }
    const subjectsHoursAllocationStep = ({
        supportType,
        selectedSubjects,
        hoursPerWeek,
        schoolSubjects,
    }) => {
        const isSubjectSupport = [
            "subject_support",
            "Soutien sur une ou plusieurs matières",
        ].includes(supportType)
        selectedSubjects = selectedSubjects || []

        return {
            label: "Répartition des heures",
            name: "subject_hours_allocation",
            type: "custom",
            is_masked: (() => {
                if (!isSubjectSupport) return true

                // Skip step if there's only one mathematical possibility:
                // 1. Single subject selected (all hours go to that subject)
                // 2. Number of subjects equals hours per week (1h each)
                const shouldSkipHourAllocation =
                    selectedSubjects.length === 1 || // Single subject: all hours to it
                    selectedSubjects.length === hoursPerWeek // Equal distribution: 1h each

                return shouldSkipHourAllocation // Hide step when no choice needed
            })(),
            component: HourAllocationStep,
            componentProps: {
                hoursPerWeek,
                selectedSubjects,
                schoolSubjects,
                generateDefaultSubjectHoursAllocation,
            },
            defaultValue: (() => {
                if (!isSubjectSupport) return {}
                if (selectedSubjects.length === 0) return {}

                // Generate equal distribution as default
                const baseHours = Math.floor(
                    hoursPerWeek / selectedSubjects.length
                )
                const extraHours = hoursPerWeek % selectedSubjects.length

                const allocation = {}
                selectedSubjects.forEach((subjectId, index) => {
                    allocation[subjectId] =
                        baseHours + (index < extraHours ? 1 : 0)
                })
                return allocation
            })(),
            validationRules: {
                customCondition: (value) => {
                    if (!isSubjectSupport) return true
                    if (selectedSubjects.length === 0) return true

                    const totalHours = selectedSubjects.reduce(
                        (sum, subjectId) => {
                            return sum + (value?.[subjectId] || 0)
                        },
                        0
                    )
                    return totalHours === hoursPerWeek
                },
            },
            description: `Répartissez vos ${hoursPerWeek} heures hebdomadaires entre vos matières`,
            bottomComponent: (
                <TextM
                    color={theme.colors.grey}
                    fontSize={12}
                    wrap
                    style={{ marginTop: 10, textAlign: "center" }}
                >
                    Chaque matière doit avoir au moins 1h
                </TextM>
            ),
            showNextButton: true,
        }
    }

    // ------------------------------------------------------------
    // Available slots step (not tested)
    // ------------------------------------------------------------
    const availableSlotsStep = ({ user, hoursPerWeek, offerType }) => {
        return {
            label: `Disponibilités de ${user?.firstname} pour les cours Masteur`,
            name: "available_slots",
            type: "custom",
            description: `Vos disponibilités actuelles (vert). Touchez pour marquer indisponible`,
            defaultValue: NEW_STUDENT_ALL_POSSIBLE_SLOTS,
            component: ConstraintsGridStep,
            componentProps: {
                user,
                hoursPerWeek,
                includeSunday: offerType === "integral",
            },
            showNextButton: true,
        }
    }

    // ************************************************************
    // ************************************************************
    // UTILITIES
    // ************************************************************
    // ************************************************************

    const isStartingNextMonth = (startDate) => {
        const startDateMoment = moment(startDate, "YYYY-MM-DD HH:mm")
        if (!startDateMoment.isValid()) return false

        const currentMonth = moment().month()
        const startMonth = startDateMoment.month()

        // If starting next month or later, return true
        if (startMonth > currentMonth) return true

        return false
    }

    // ------------------------------------------------------------
    // First month hours
    // ------------------------------------------------------------
    // La règle vit dans `@common/utils/partialMonthHours` — miroir du back, qui
    // décide le montant prélevé. Ce hook ne fait que l'exposer aux quatre écrans
    // qui la lisaient à travers lui.
    const calculateFirstMonthHours = partialMonthHours

    // ------------------------------------------------------------
    // Hours per month from hours per week
    // ------------------------------------------------------------
    const getHoursPerMonthFromHoursPerWeek = (
        sessionsPerWeek,
        hoursPerWeek
    ) => {
        return Object.keys(sessionsPerWeek || {})?.find(
            (hoursPerMonth) => sessionsPerWeek?.[hoursPerMonth] === hoursPerWeek
        )
    }
    const getHoursPerWeekFromHoursPerMonth = (sessionsPerWeek, hoursPerMonth) =>
        sessionsPerWeek?.[hoursPerMonth]

    return {
        offerTypeStep,
        offerPlanStep,
        calculateOffersOptions,
        tarifStep,
        subscriptionStartDateStep,
        schoolSubjectsStep,
        subjectsHoursAllocationStep,
        availableSlotsStep,
        calculateFirstMonthHours,
        getHoursPerMonthFromHoursPerWeek,
        getHoursPerWeekFromHoursPerMonth,
        generateDefaultSubjectHoursAllocation,
        isStartingNextMonth,
    }
}

// All possible slots (fallback)
export const NEW_STUDENT_ALL_POSSIBLE_SLOTS = (() => {
    const mondayTuesdayThursdayFriday = ["17h-18h", "18h-19h", "19h-20h"]
    const wednesday = [
        "14h-15h",
        "15h-16h",
        "16h-17h",
        "17h-18h",
        "18h-19h",
        "19h-20h",
    ]
    const saturday = ["09h-10h", "10h-11h", "11h-12h", "14h-15h", "15h-16h"]
    const days = [
        { day: "Monday", times: mondayTuesdayThursdayFriday },
        { day: "Tuesday", times: mondayTuesdayThursdayFriday },
        { day: "Wednesday", times: wednesday },
        { day: "Thursday", times: mondayTuesdayThursdayFriday },
        { day: "Friday", times: mondayTuesdayThursdayFriday },
        { day: "Saturday", times: saturday },
    ]
    return days.flatMap((d) => d.times.map((t) => `${d.day} ${t}`))
})()
